import React, { useState, useEffect } from 'react';
import './ProfileSettingsModal.css';

function ProfileSettingsModal({ isOpen, onClose, user, onUpdate }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    profile_avatar: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        profile_avatar: user.profile_avatar || 'boy-1.svg',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate current password is provided
    if (!formData.current_password) {
      setError('Current password is required to update profile');
      setLoading(false);
      return;
    }

    // Validate new password if provided
    if (formData.new_password) {
      if (formData.new_password.length < 8) {
        setError('New password must be at least 8 characters');
        setLoading(false);
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        setError('New passwords do not match');
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch('/backend/api/auth/update-profile.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Profile updated successfully!');
        setFormData({
          ...formData,
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        
        // Call parent update handler
        if (onUpdate) {
          onUpdate(data.user);
        }
        
        // Close modal after 1.5 seconds
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-settings-modal-overlay" onClick={onClose}>
      <div className="profile-settings-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Profile Settings</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="profile-settings-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <h3>Personal Information</h3>
            
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Profile Avatar</label>
              <select
                name="profile_avatar"
                value={formData.profile_avatar}
                onChange={handleChange}
              >
                <option value="boy-1.svg">Boy 1</option>
                <option value="girl-1.svg">Girl 1</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                Network ID
                <span 
                  className="info-icon"
                  onMouseEnter={() => setShowInfoTooltip(true)}
                  onMouseLeave={() => setShowInfoTooltip(false)}
                >
                  ⓘ
                </span>
                {showInfoTooltip && (
                  <span className="info-tooltip">Network ID cannot be changed</span>
                )}
              </label>
              <input
                type="text"
                value={user?.network_id || 'Not set'}
                disabled
                className="readonly-input"
              />
            </div>
          </div>

          {/* Security Section */}
          <div className="form-section">
            <h3>Security</h3>
            
            <div className="form-group">
              <label>Current Password *</label>
              <input
                type="password"
                name="current_password"
                value={formData.current_password}
                onChange={handleChange}
                placeholder="Required to save changes"
                required
              />
            </div>

            <div className="form-group">
              <label>New Password (optional)</label>
              <input
                type="password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder="Confirm new password"
                disabled={!formData.new_password}
              />
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="save-button">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileSettingsModal;




